import React, { useState } from 'react';
import { useQuery } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Queue as QueueIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { useOrganization } from '../../contexts/OrganizationContext';
import KpiReport from './KpiReport';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AnalyticsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState('today');
  const { selectedOrgId } = useOrganization();

  // Загрузка данных
  const { data: analyticsData, isLoading, error, refetch } = useQuery(
    ['analytics', 'overview', selectedOrgId, period],
    async () => {
      const res = await apiClient.get('/api/analytics/overview', {
        params: { period }
      });
      return res.data;
    },
    { refetchInterval: 60000 }
  );

  const { data: queuesData } = useQuery(['queues', selectedOrgId], async () => {
    const res = await apiClient.get('/api/queues');
    return res.data;
  });

  const { data: ticketsData } = useQuery(['tickets', selectedOrgId], async () => {
    const res = await apiClient.get('/api/tickets');
    return res.data;
  });

  const { data: operatorsData } = useQuery(['operators', selectedOrgId], async () => {
    const res = await apiClient.get('/api/operators');
    return res.data;
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Аналитика и отчеты</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Ошибка загрузки аналитических данных</Alert>
      </Box>
    );
  }

  // Подготовка данных
  const analyticsOverview = analyticsData?.data;

  const queues = queuesData?.data?.queues || [];
  const tickets = ticketsData?.data?.tickets || [];
  const operators = operatorsData?.data?.operators || [];

  const totalTicketsToday =
    analyticsOverview?.ticketsTotal ??
    tickets.length ??
    0;
  const totalTicketsPreviousDay = analyticsOverview?.ticketsTotalPreviousDay ?? null;
  const completedTickets =
    analyticsOverview?.ticketsCompleted ??
    tickets.filter((t: any) => t.status === 'completed').length ??
    0;
  const completedTicketsPreviousDay = analyticsOverview?.ticketsCompletedPreviousDay ?? null;
  const activeOperators =
    analyticsOverview?.operatorsActive ??
    operators.filter((op: any) => op.status !== 'offline').length ??
    0;
  const avgWaitTime =
    analyticsOverview?.avgWaitTime ??
    0;
  const avgWaitTimePreviousDay = analyticsOverview?.avgWaitTimePreviousDay ?? null;
  const prevHourStats = analyticsOverview?.previousHour;

  const calcPercentChange = (current: number | null, previous: number | null) => {
    if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) {
      return null;
    }
    const diff = ((current - previous) / previous) * 100;
    return Number(diff.toFixed(1));
  };

  const ticketsChange = calcPercentChange(totalTicketsToday, totalTicketsPreviousDay);
  const completedChange = calcPercentChange(completedTickets, completedTicketsPreviousDay);
  const waitTimeChange = calcPercentChange(avgWaitTime, avgWaitTimePreviousDay);
  const operatorsChange = calcPercentChange(activeOperators, prevHourStats?.activeOperators ?? null);

  const formatChangeText = (change: number | null, suffix: string) =>
    change !== null && change !== undefined && change !== 0 ? `${change > 0 ? '+' : ''}${change}${suffix}` : null;

  const ticketsChangeText = formatChangeText(ticketsChange, '% к вчера');
  const completedChangeText = formatChangeText(completedChange, '% к вчера');
  const waitTimeChangeText = formatChangeText(waitTimeChange, '% к вчера');
  const operatorsChangeText = formatChangeText(operatorsChange, '% к предыдущему часу');

  const hourlyData =
    analyticsOverview?.ticketsByHour?.length
      ? analyticsOverview.ticketsByHour
        .sort((a: any, b: any) => a.hour - b.hour)
        .map((item: any) => ({
          hour: `${item.hour.toString().padStart(2, '0')}:00`,
          tickets: item.created,
          completed: item.completed,
          avgWaitTime: Number(item.avgWaitTime ?? 0),
          activeOperators: Number(item.activeOperators ?? 0),
        }))
      : Array.from({ length: 24 }, (_, i) => ({
        hour: `${i.toString().padStart(2, '0')}:00`,
        tickets: 0,
        completed: 0,
        avgWaitTime: 0,
        activeOperators: 0,
      }));

  const statusDistributionSource = analyticsOverview?.statusDistribution || {};

  const waitingCombined =
    (statusDistributionSource.waiting || 0) +
    (statusDistributionSource.called || 0);
  const inServiceCount = statusDistributionSource.in_service || 0;
  const completedCount = statusDistributionSource.completed || 0;
  const cancelledCombined =
    (statusDistributionSource.cancelled || 0) +
    (statusDistributionSource.no_show || 0) +
    (statusDistributionSource.redirected || 0);

  const statusDistribution = [
    { name: 'Завершено', value: completedCount, color: '#4caf50' },
    { name: 'В ожидании', value: waitingCombined, color: '#ff9800' },
    { name: 'Обслуживается', value: inServiceCount, color: '#2196f3' },
    { name: 'Отменено', value: cancelledCombined, color: '#f44336' },
  ];

  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 85) return 'success';
    if (efficiency >= 60) return 'warning';
    return 'error';
  };

  const queuePerformance =
    analyticsOverview?.queuePerformance?.length
      ? analyticsOverview.queuePerformance.map((queue: any) => ({
        ...queue,
        completedToday: Number(queue.completedToday ?? 0),
        waitingCount: Number(queue.waitingCount ?? 0),
        avgServiceTimeMinutes: Number(queue.avgServiceTimeMinutes ?? 0),
        totalToday: Number(queue.totalToday ?? 0),
        efficiency: Number(queue.efficiency ?? 0),
        activeOperators: Number(queue.activeOperators ?? 0),
      }))
      : queues.map((queue: any) => {
        const waiting = queue.waiting_count || 0;
        const completed = queue.completed_today || 0;
        const total = waiting + completed;
        return {
          id: queue.id,
          name: queue.name,
          completedToday: completed,
          waitingCount: waiting,
          avgServiceTimeMinutes: Number(queue.avgServiceTimeToday || 0),
          totalToday: total,
          efficiency: total > 0 ? Math.round((completed / total) * 100) : 0,
          activeOperators: queue.active_operators || 0,
        };
      });

  return (
    <>
      <Helmet>
        <title>Аналитика и отчеты - ELEKTROM</title>
      </Helmet>

      <Box>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Аналитика и отчеты
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Детальная аналитика работы системы очередей
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Период</InputLabel>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                label="Период"
              >
                <MenuItem value="today">Сегодня</MenuItem>
                <MenuItem value="week">Неделя</MenuItem>
                <MenuItem value="month">Месяц</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Основные KPI */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Всего талонов</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{totalTicketsToday}</Typography>
                    {ticketsChangeText && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TrendingUpIcon
                          sx={{ fontSize: 16, mr: 0.5, transform: ticketsChange && ticketsChange < 0 ? 'rotate(180deg)' : 'none' }}
                        />
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{ticketsChangeText}</Typography>
                      </Box>
                    )}
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Завершено</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{completedTickets}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUpIcon
                        sx={{ fontSize: 16, mr: 0.5, transform: completedChange && completedChange < 0 ? 'rotate(180deg)' : 'none' }}
                      />
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {Math.round((completedTickets / Math.max(totalTicketsToday, 1)) * 100)}% успешно
                      </Typography>
                    </Box>
                    {completedChangeText && (
                      <Typography variant="body2" sx={{ opacity: 0.6 }}>
                        {completedChangeText}
                      </Typography>
                    )}
                  </Box>
                  <AssessmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Активные операторы</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{activeOperators}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>из {operators.length}</Typography>
                    {operatorsChangeText && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TrendingUpIcon
                          sx={{ fontSize: 16, mr: 0.5, transform: operatorsChange && operatorsChange < 0 ? 'rotate(180deg)' : 'none' }}
                        />
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{operatorsChangeText}</Typography>
                      </Box>
                    )}
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Среднее ожидание</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{avgWaitTime.toFixed(1)}</Typography>
                    {waitTimeChangeText && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TrendingDownIcon
                          sx={{
                            fontSize: 16,
                            mr: 0.5,
                            transform: waitTimeChange && waitTimeChange > 0 ? 'rotate(180deg)' : 'none'
                          }}
                        />
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{waitTimeChangeText}</Typography>
                      </Box>
                    )}
                  </Box>
                  <TimerIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Табы с детальной аналитикой */}
        <Card elevation={3}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="Общая статистика" icon={<AssessmentIcon />} />
              <Tab label="По очередям" icon={<QueueIcon />} />
              <Tab label="Тренды" icon={<TimelineIcon />} />
              <Tab label="KPI Отчет" icon={<AssessmentIcon />} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Typography variant="h6" gutterBottom>
                  Активность по часам
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="tickets" stackId="1" stroke="#2196F3" fill="#2196F3" fillOpacity={0.6} name="Создано талонов" />
                    <Area type="monotone" dataKey="completed" stackId="1" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.6} name="Завершено" />
                  </AreaChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Typography variant="h6" gutterBottom>
                  Распределение по статусам
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Производительность очередей
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Очередь</TableCell>
                    <TableCell align="center">Обслужено</TableCell>
                    <TableCell align="center">В ожидании</TableCell>
                    <TableCell align="center">Среднее время</TableCell>
                    <TableCell align="center">Эффективность</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queuePerformance.map((queue: any) => (
                    <TableRow key={queue.id || queue.name}>
                      <TableCell>{queue.name}</TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="success.main">
                          {queue.completedToday}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="warning.main">
                          {queue.waitingCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {Number(queue.avgServiceTimeMinutes || 0).toFixed(1)} мин
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${queue.efficiency}%`}
                          color={getEfficiencyColor(queue.efficiency)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Тренды производительности
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="tickets" stroke="#2196F3" strokeWidth={3} name="Количество талонов" />
                <Line type="monotone" dataKey="completed" stroke="#4CAF50" strokeWidth={3} name="Завершено" />
              </LineChart>
            </ResponsiveContainer>

            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Ключевые показатели
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Средние талоны в час:</Typography>
                      <Typography variant="h5" color="primary.main">
                        {hourlyData.length ? (totalTicketsToday / hourlyData.length).toFixed(1) : '0.0'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Пиковый час:</Typography>
                      <Typography variant="h5" color="warning.main">
                        {hourlyData.length
                          ? hourlyData.reduce((peak, entry) => (entry.tickets > peak.tickets ? entry : peak), hourlyData[0]).hour
                          : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Уровень завершения:</Typography>
                      <Typography variant="h5" color="success.main">
                        {Math.round((completedTickets / Math.max(totalTicketsToday, 1)) * 100)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Средняя эффективность:</Typography>
                      <Typography variant="h5" color="success.main">
                        {queuePerformance.length
                          ? Math.round(
                            queuePerformance.reduce((sum: number, queue: any) => sum + (queue.efficiency || 0), 0) /
                            queuePerformance.length
                          )
                          : 0}
                        %
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Рекомендации
                  </Typography>
                  {(() => {
                    const alerts: { severity: 'info' | 'warning' | 'success'; message: string }[] = [];
                    const lowestEfficiencyQueue = queuePerformance.length
                      ? queuePerformance.reduce((worst: any, queue: any) =>
                        !worst || (queue.efficiency || 0) < (worst.efficiency || 0) ? queue : worst,
                        null)
                      : null;
                    const highestWaitingQueue = queuePerformance.length
                      ? queuePerformance.reduce((maxQueue: any, queue: any) =>
                        !maxQueue || (queue.waitingCount || 0) > (maxQueue.waitingCount || 0) ? queue : maxQueue,
                        null)
                      : null;

                    if (avgWaitTimePreviousDay !== null && waitTimeChange !== null && waitTimeChange > 0) {
                      alerts.push({
                        severity: 'warning',
                        message: `Среднее ожидание увеличилось на ${waitTimeChange}% по сравнению со вчера. Рассмотрите перераспределение операторов.`
                      });
                    }

                    if (avgWaitTime > 15) {
                      alerts.push({
                        severity: 'warning',
                        message: 'Среднее ожидание превышает 15 минут. Запланируйте усиление команды в пиковые часы.'
                      });
                    }

                    if (lowestEfficiencyQueue && (lowestEfficiencyQueue.efficiency || 0) < 60) {
                      alerts.push({
                        severity: 'warning',
                        message: `Очередь "${lowestEfficiencyQueue.name}" показывает низкую эффективность (${lowestEfficiencyQueue.efficiency}%).`
                      });
                    }

                    if (highestWaitingQueue && (highestWaitingQueue.waitingCount || 0) > 0) {
                      alerts.push({
                        severity: 'info',
                        message: `Наибольшее ожидание сейчас в очереди "${highestWaitingQueue.name}" — ${highestWaitingQueue.waitingCount} клиентов.`
                      });
                    }

                    if (!alerts.length) {
                      alerts.push({
                        severity: completedTickets > 0 ? 'success' : 'info',
                        message: completedTickets > 0
                          ? 'Система работает стабильно: критических отклонений не обнаружено.'
                          : 'Недостаточно данных за сегодня для формирования рекомендаций.'
                      });
                    }

                    return alerts.map((alert, index) => (
                      <Alert key={index} severity={alert.severity} sx={{ mb: index < alerts.length - 1 ? 2 : 0 }}>
                        {alert.message}
                      </Alert>
                    ));
                  })()}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <KpiReport />
          </TabPanel>
        </Card>


      </Box>
    </>
  );
};
